'use client'

import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react'
import { MoreMenuIcon } from '../../icons'
import {
    ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS,
    resolveIconButtonAriaLabel,
} from '../../../constants/iconButtonAriaLabelMessageKeys'
import { createPortal } from 'react-dom'
import { Button, ButtonVariants } from '../../atoms'

import { getFocusableElementsInOverlay, useAccessibleOverlay } from '../../../hooks/useAccessibleOverlay'
import { useOnClickOutside } from '../../../hooks/useOutsideClick'
import { DictionaryContext } from '../../../providers'
import { useWindowDimensions } from '../../../hooks/useWindowDimensions'
import { documentExists } from '../../../../../MyStichtingOpenHelpers/src/static'
import { ContextMenuInteractionMode, ContextMenuInteractionProvider } from './ContextMenuInteractionContext'

const scheduleFocusFirstFocusableChild = (contentRef: React.RefObject<HTMLDivElement | null>): (() => void) => {
    const frame = requestAnimationFrame(() => {
        const container = contentRef.current
        if (!container) return
        const items = getFocusableElementsInOverlay(container)
        items[0]?.focus()
    })
    return () => cancelAnimationFrame(frame)
}

const subscribeResizeAndScrollToClosePanel = (
    onClose: () => void,
    contentRef: React.RefObject<HTMLDivElement | null>
): (() => void) => {
    const controller = new AbortController()
    const { signal } = controller
    const handleScrollCapture = (event: Event) => {
        const target = event.target
        if (target instanceof Node && contentRef.current?.contains(target)) {
            return
        }
        onClose()
    }
    window.addEventListener('resize', onClose, { signal })
    document.addEventListener('scroll', handleScrollCapture, { signal, capture: true })
    return () => controller.abort()
}

/**
 * Props for the context menu / disclosure panel.
 *
 * **Interaction modes (`interactionMode`)**
 * - **`menu` (default):** W3C APG menu pattern — `role="menu"`, arrow keys between items, `menuitem` + `tabIndex={-1}` on items; **Tab** closes the panel and returns focus to the trigger. Use for table row actions and similar command menus.
 * - **`dialog`:** Same overlay pattern as main navigation flyouts — `role="dialog"`, non-modal, **Tab / Shift+Tab** cycles inside the panel (focus trap); **Escape** closes. Use next to the header when keyboard behavior should match the rest of the header chrome.
 */
export interface ContextMenuProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode
    align?: 'left' | 'right'
    toggleButton?: React.ReactElement
    /**
     * How keyboard users move through the panel. Defaults to `menu` (arrow keys; Tab dismisses).
     * Use `dialog` for Tab focus trapping like main navigation submenus.
     */
    interactionMode?: ContextMenuInteractionMode
}

export const ContextMenu = ({
    children,
    align = 'right',
    toggleButton,
    className,
    interactionMode = 'menu',
}: ContextMenuProps) => {
    const dictionary = useContext(DictionaryContext)
    const reactId = useId()
    const contentId = `${reactId}-context-menu`
    const [shouldShowMenu, setShouldShowMenu] = useState(false)
    const previousShouldShowMenuRef = useRef(false)

    const menuAriaLabel = resolveIconButtonAriaLabel(dictionary, ICON_BUTTON_ARIA_LABEL_MESSAGE_KEYS.OPEN_CONTEXT_MENU)

    const overlay = useAccessibleOverlay({
        isOpen: shouldShowMenu,
        contentId,
        role: interactionMode,
        trapFocus: interactionMode === 'dialog',
        ariaLabel: menuAriaLabel,
        ariaModal: false,
    })

    const windowDimensions = useWindowDimensions()

    const toggleContextMenu = useCallback(() => {
        setShouldShowMenu((previous) => !previous)
    }, [])

    useEffect(() => {
        if (previousShouldShowMenuRef.current && !shouldShowMenu) {
            overlay.focusTrigger()
        }
        previousShouldShowMenuRef.current = shouldShowMenu
    }, [shouldShowMenu, overlay.focusTrigger])

    useEffect(() => {
        if (!shouldShowMenu || !documentExists()) return undefined
        const cancelFocusFrame = scheduleFocusFirstFocusableChild(overlay.contentRef)
        const cancelViewportDismiss = subscribeResizeAndScrollToClosePanel(
            () => setShouldShowMenu(false),
            overlay.contentRef
        )
        return () => {
            cancelFocusFrame()
            cancelViewportDismiss()
        }
    }, [shouldShowMenu, overlay.contentRef])

    const handleOutsideClick = useCallback(() => {
        if (shouldShowMenu) {
            setShouldShowMenu(false)
        }
    }, [shouldShowMenu])

    useOnClickOutside(overlay.contentRef, handleOutsideClick, 'mousedown', [overlay.triggerRef])

    const rect = overlay.triggerRef.current?.getBoundingClientRect()

    const { onKeyDown: overlayKeyDown, ...restContentProps } = overlay.contentProps

    const handleOverlayKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            setShouldShowMenu(false)
            return
        }
        if (interactionMode === 'menu' && e.key === 'Tab') {
            e.preventDefault()
            setShouldShowMenu(false)
            return
        }
        overlayKeyDown?.(e)
    }

    const handleItemsAreaClick = () => {
        if (shouldShowMenu && interactionMode === 'menu') {
            setShouldShowMenu(false)
        }
    }

    const contextMenuRoot = documentExists() ? document.getElementById('context-menu-root') : null

    const portalStyle: { top?: string; left?: string; right?: string } = {
        top: `${rect?.bottom}px`,
    }
    if (align === 'right') {
        portalStyle.right = `${rect?.right ? (windowDimensions?.width ?? 0) - rect.right : 0}px`
    } else {
        portalStyle.left = `${rect?.left}px`
    }

    /** Renders the menu trigger: custom `toggleButton` with merged ref/ARIA, or the default icon button. */
    const renderToggle = () => {
        if (toggleButton) {
            const child = toggleButton as React.ReactElement<React.HTMLAttributes<HTMLElement>> & {
                ref?: React.Ref<HTMLElement>
            }
            const childRef = child.ref

            // Callback ref: keep both our overlay trigger ref (focus return, outside-click ignore) and the caller's ref.
            const setRef = (el: HTMLElement | null) => {
                const triggerRefMutable = overlay.triggerRef as React.MutableRefObject<HTMLElement | null>
                triggerRefMutable.current = el
                if (typeof childRef === 'function') {
                    const forwardRefCallback = childRef as (element: HTMLElement | null) => void
                    forwardRefCallback(el)
                } else if (childRef && typeof childRef === 'object' && 'current' in childRef) {
                    const mutableChildRef = childRef as React.MutableRefObject<HTMLElement | null>
                    mutableChildRef.current = el
                }
            }

            // cloneElement: TypeScript expects a concrete type so `ref` is allowed in the patch object.
            return React.cloneElement(
                child as React.ReactElement<
                    React.ButtonHTMLAttributes<HTMLButtonElement> & { ref?: React.Ref<HTMLElement> }
                >,
                {
                    ...overlay.triggerAriaProps,
                    ref: setRef,
                    // Chain: caller's onClick (if any), then toggle visibility.
                    onClick: (e: React.MouseEvent<HTMLElement>) => {
                        const originalOnClick = child.props.onClick
                        if (typeof originalOnClick === 'function') {
                            originalOnClick(e as React.MouseEvent<HTMLButtonElement>)
                        }
                        toggleContextMenu()
                    },
                }
            )
        }

        return (
            <Button
                ref={overlay.triggerRef as React.Ref<HTMLButtonElement>}
                data-testid="context-menu-toggle"
                variant={ButtonVariants.QUIET}
                icon={<MoreMenuIcon />}
                onClick={toggleContextMenu}
                {...overlay.triggerAriaProps}
                aria-label={menuAriaLabel}
            />
        )
    }

    return (
        <>
            <div className="inline-block">{renderToggle()}</div>

            {shouldShowMenu &&
                documentExists() &&
                windowDimensions &&
                createPortal(
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role is menu or dialog; keyboard from W3C APG overlay patterns
                    <div
                        ref={overlay.contentRef}
                        {...restContentProps}
                        className={`context-menu inline-flex flex-col p-1 ${className ?? ''}`}
                        style={portalStyle}
                        onKeyDown={handleOverlayKeyDown}
                        role={interactionMode}
                        tabIndex={-1}
                    >
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- closes menu after item click via event bubbling */}
                        <ContextMenuInteractionProvider value={interactionMode}>
                            <div role="presentation" onClick={handleItemsAreaClick}>
                                {children}
                            </div>
                        </ContextMenuInteractionProvider>
                    </div>,
                    contextMenuRoot ?? document.body
                )}
        </>
    )
}
